/**
 * TaskAssignmentService
 * Service for TaskAssignment entity operations
 * Route: api/task-assignment
 */
import { CrudService } from '../base/CrudService';
/**
 * TaskAssignmentService
 * Handles task assignment CRUD operations and custom assignment queries
 */
export class TaskAssignmentService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TaskAssignmentService.DEFAULT_SERVICE_PATH,
            basePath: TaskAssignmentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // Override getById to handle composite key (taskId, userId)
    /**
     * Get assignment by task ID and user ID
     * GET api/task-assignment/{taskId}/{userId}
     */
    async getByIds(taskId, userId) {
        const response = await this.getByIdsRaw(taskId, userId);
        if (!response.data) {
            throw new Error(`Assignment not found for task ${taskId} and user ${userId}`);
        }
        return response.data;
    }
    /**
     * Get assignment by task ID and user ID with full response
     */
    getByIdsRaw(taskId, userId) {
        const url = this.buildUrl(`${taskId}/${userId}`);
        return this.http.get(url);
    }
    // ==================== GET BY TASK ID ====================
    /**
     * Get all assignments for a task
     * GET api/task-assignment/task/{taskId}
     */
    async getByTaskId(taskId) {
        const response = await this.getByTaskIdRaw(taskId);
        return response.data ?? [];
    }
    /**
     * Get all assignments for a task with full response
     */
    getByTaskIdRaw(taskId) {
        const endpoint = this.getEndpoint('by-task', `task/${taskId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET VIEW BY TASK ID ====================
    /**
     * Get assignment views for a task with user details
     * GET api/task-assignment/task/{taskId}/view
     */
    async getViewByTaskId(taskId) {
        const response = await this.getViewByTaskIdRaw(taskId);
        return response.data ?? [];
    }
    /**
     * Get assignment views for a task with full response
     */
    getViewByTaskIdRaw(taskId) {
        const endpoint = this.getEndpoint('view-by-task', `task/${taskId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET VIEW BY IDS ====================
    /**
     * Get assignment view with user details
     * GET api/task-assignment/{taskId}/{userId}/view
     */
    async getViewByIds(taskId, userId) {
        const response = await this.getViewByIdsRaw(taskId, userId);
        if (!response.data) {
            throw new Error(`Assignment view not found for task ${taskId} and user ${userId}`);
        }
        return response.data;
    }
    /**
     * Get assignment view with user details with full response
     */
    getViewByIdsRaw(taskId, userId) {
        const endpoint = this.getEndpoint('view-by-ids', `${taskId}/${userId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // Override delete to handle composite key
    /**
     * Delete assignment (remove user from task)
     * DELETE api/task-assignment/{taskId}/{userId}
     */
    async deleteByIds(taskId, userId) {
        const response = await this.deleteByIdsRaw(taskId, userId);
        return response.success;
    }
    /**
     * Delete assignment with full response
     */
    deleteByIdsRaw(taskId, userId) {
        const url = this.buildUrl(`${taskId}/${userId}`);
        return this.http.delete(url);
    }
    // ==================== REMOVE (Alternative DELETE) ====================
    /**
     * Remove user from task (alternative endpoint)
     * POST api/task-assignment/{taskId}/{userId}/remove
     */
    async remove(taskId, userId) {
        const response = await this.removeRaw(taskId, userId);
        return response.success;
    }
    /**
     * Remove user from task with full response
     */
    removeRaw(taskId, userId) {
        const endpoint = this.getEndpoint('remove', `${taskId}/${userId}/remove`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
TaskAssignmentService.DEFAULT_SERVICE_PATH = 'tasks';
TaskAssignmentService.BASE_PATH = 'api/task-assignment';
//# sourceMappingURL=TaskAssignmentService.js.map